import React, { forwardRef, Ref, useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import { RevenuePeriod } from 'src/components/tax/ReportRevenue/RevenuePeriod';
import {
  BucketData,
  BucketError,
  OccupancyError,
  PaymentPeriod,
  TaxDataError,
  TaxDataModel,
  TaxLicenseError,
  TaxPeriodError,
} from 'src/types/tax';
import { Form, FormInstance } from 'rsuite';
import {
  validateBuckets,
  validateOccupancy,
} from 'src/components/tax/ReportRevenue/validators';
import dayjs from 'dayjs';
import { roundDecimalPlaces } from 'src/common/utils';
import { TaxConfigurationDeductions } from 'src/types/configuration';

interface TaxReportContentProps {
  bucketPerPeriod: Record<string, BucketData[]>;
  deductions?: TaxConfigurationDeductions;
  isMobile?: boolean;
  licenses: Array<PaymentPeriod>;
  onSubmit: (data: TaxDataModel) => void;
}

export const TaxReportContent = forwardRef(
  (
    {
      bucketPerPeriod,
      deductions,
      isMobile,
      licenses,
      onSubmit,
    }: TaxReportContentProps,
    ref: Ref<FormInstance>
  ) => {
    const daysInPeriod = useCallback(
      ({ startDate, endDate }: PaymentPeriod) => {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        return end.diff(start, 'day') + 1; // Adding 1 to include both start and end days
      },
      []
    );

    const formPeriods = useMemo(() => {
      const periods = licenses.map((selectedLicense) => {
        const licBuckets = bucketPerPeriod[selectedLicense.startDate];
        return {
          licenses: selectedLicense.licenses.map((lic) => {
            return {
              buckets:
                lic.buckets ||
                licBuckets.map((_, index) => ({
                  provider: licBuckets[index].key,
                  enableDeductions: deductions?.enable,
                })),
              occupancy: lic.occupancy || {},
            };
          }),
        };
      });
      return { periods };
    }, [licenses, bucketPerPeriod, deductions]);

    const taxFlowForm = useFormik<TaxDataModel>({
      initialValues: formPeriods,
      validateOnChange: true,
      onSubmit,
      validate: (values): TaxDataError => {
        let errorFound = false;
        const periodsErr: Array<TaxPeriodError> = (values.periods ?? []).map(
          (period, index): TaxPeriodError => {
            const days = daysInPeriod(licenses[index]);
            const formLicenses: Array<TaxLicenseError> = period.licenses?.map(
              (lic) => {
                const bucketError: Array<BucketError> = validateBuckets(
                  lic.buckets,
                  deductions?.enable
                );
                const occupancy: OccupancyError = validateOccupancy(
                  lic.occupancy,
                  days
                );
                if (!!occupancy.available || !!occupancy.occupied) {
                  errorFound = true;
                }

                if (
                  bucketError.some((bErr) => !!bErr.discounts || !!bErr.revenue)
                ) {
                  errorFound = true;
                }
                return { buckets: bucketError, occupancy };
              }
            );

            return { licenses: formLicenses };
          }
        );
        if (!errorFound) {
          return {};
        }

        return { periods: periodsErr };
      },
    });

    const handleChange = useCallback(
      (_: TaxDataModel, event?: React.ChangeEvent<HTMLInputElement>) => {
        if (
          event?.target.name.includes('discounts') ||
          event?.target.name.includes('revenue')
        ) {
          let value = event?.target.value;
          if (
            !value.endsWith('.') &&
            !value.endsWith('.0') &&
            !value.endsWith('.00')
          ) {
            value = (roundDecimalPlaces(Number(value)) || 0).toString();
          }

          event.target.value = value;
          taxFlowForm.handleChange(event);
        } else {
          taxFlowForm.handleChange(event);
        }
      },
      [taxFlowForm]
    );

    return (
      <Form
        formValue={taxFlowForm.values}
        nestedField
        onChange={(_, event) =>
          handleChange(_, event as React.ChangeEvent<HTMLInputElement>)
        }
        onSubmit={(_, event?: React.FormEvent<HTMLFormElement>) => {
          taxFlowForm?.handleSubmit(event);
        }}
        ref={ref}
      >
        {taxFlowForm.values?.periods?.map((p, index) => (
          <RevenuePeriod
            {...licenses[index]}
            buckets={bucketPerPeriod[licenses[index].startDate]}
            error={
              taxFlowForm.errors.periods?.[index] as unknown as TaxPeriodError
            }
            label={licenses[index].name}
            key={`periods[${index}]`}
            name={`periods[${index}]`}
            value={p.licenses || []}
            isMobile={isMobile}
          />
        ))}
      </Form>
    );
  }
);
